// src/utils/videoClaim.js
//
// Pass 71 — the ONE canonical, ATOMIC instructor video-claim writer. The legacy Video Queue claimed with a plain
// updateDoc (last-write-wins) so two instructors could both "succeed" and silently overwrite each other. This
// claim runs inside a transaction: it re-reads the video, verifies it is still genuinely claimable against the
// canonical read-model, and only then assigns it to the caller. A second concurrent claimer reads the now-assigned
// doc and is rejected — exactly one winner, no silent reassignment.

import { runTransaction, doc, serverTimestamp } from 'firebase/firestore';
import { isAvailableToClaim, isAssignedVideo, getVideoAssignedInstructorAuthId } from './videoWorkflow';

const str = (v) => (v == null ? '' : String(v).trim());
const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };

export const VIDEO_CLAIM_TAKEN = 'This video has already been claimed by another instructor.';
export const VIDEO_CLAIM_NOT_CLAIMABLE = 'This video is not available to claim.';
export const VIDEO_CLAIM_ACTOR_REQUIRED = 'You must be signed in to claim a video.';
export const VIDEO_CLAIM_WRONG_ORG = 'This video belongs to a different organisation.';

// claimVideoForInstructor(db, videoId, { actorAuthUserId, organizationId }) → { videoId, claimed, alreadyMine }
// Assignment is an Auth uid (frozen semantic): both assignedInstructorId and assignedTo are set to the caller.
// Pass 71A (§22) — defense-in-depth: when the caller passes their selected/current organizationId, the
// transaction verifies the video actually belongs to it (Firestore Rules remain the authority; this fails fast).
export async function claimVideoForInstructor(db, videoId, { actorAuthUserId = '', organizationId = '' } = {}) {
  const id = str(videoId);
  const uid = str(actorAuthUserId);
  const org = str(organizationId);
  if (!id) throw fail('VIDEO_CLAIM_ID_REQUIRED', 'A video id is required.');
  if (!uid) throw fail('VIDEO_CLAIM_ACTOR_REQUIRED', VIDEO_CLAIM_ACTOR_REQUIRED);

  let claimed = false;
  let alreadyMine = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'videos', id);
    const snap = await tx.get(ref);
    if (!snap.exists || !snap.exists()) throw fail('VIDEO_CLAIM_NOT_FOUND', 'This video could not be found.');
    const video = { id, ...snap.data() };

    // idempotent: if this caller already holds the claim, succeed without a write.
    if (isAssignedVideo(video) && getVideoAssignedInstructorAuthId(video) === uid) {
      alreadyMine = true;
      return;
    }
    // someone else already holds it → refuse, never steal (no auto-reassign).
    if (isAssignedVideo(video)) throw fail('VIDEO_CLAIM_TAKEN', VIDEO_CLAIM_TAKEN);
    // tenant fail-fast: a video with no org, or a mismatched org, is not claimable from this org context.
    if (org && str(video.organizationId) !== org) throw fail('VIDEO_CLAIM_WRONG_ORG', VIDEO_CLAIM_WRONG_ORG);
    // must still be genuinely claimable (pending, real coach, upload usable, no conflict).
    if (!isAvailableToClaim(video)) throw fail('VIDEO_CLAIM_NOT_CLAIMABLE', VIDEO_CLAIM_NOT_CLAIMABLE);

    tx.set(ref, {
      assignedInstructorId: uid,
      assignedTo: uid,
      assigned: true,
      assignedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    claimed = true;
  });

  return { videoId: id, claimed, alreadyMine };
}
